import { apiRequest } from '../../shared/api/client';

/**
 * Carriers — the current product's `tblWebShipMethod` and its maintenance screen. Tenant
 * script 081.
 *
 *   GET    /api/v1/ship-methods[?includeInactive=true]   any signed-in user
 *   POST   /api/v1/ship-methods                          tenant:manage_settings
 *   PUT    /api/v1/ship-methods/{id}                     tenant:manage_settings
 *   DELETE /api/v1/ship-methods/{id}                     tenant:manage_settings
 *
 * Reading is open to any signed-in user because ship confirm, the wave shipment and the order
 * form all have to offer the list.
 *
 * Nothing is seeded: carriers live in the per-tenant database with their own screen, so each
 * customer's list is their own and a new tenant starts empty — which is how the current product
 * behaves, and what stops invented carrier names being baked into somebody's shipment history.
 */

const COOKIE_INIT: RequestInit = { credentials: 'include' };

export interface ShipMethodDto {
  shipMethodId: string;
  shipMethodCode: string;
  displayName: string;
  /** The grouping name — "UPS" across UPS Ground and UPS 2nd Day. Optional. */
  carrier: string | null;
  isActive: boolean;
  /** An order, pick or shipment names it, so it can only be deactivated, never deleted. */
  isInUse: boolean;
}

export interface ShipMethodRequest {
  shipMethodCode: string;
  displayName: string;
  carrier: string | null;
  isActive: boolean;
}

export async function listShipMethods(
  includeInactive = false,
  signal?: AbortSignal,
): Promise<ShipMethodDto[]> {
  const query = includeInactive ? '?includeInactive=true' : '';
  const { data } = await apiRequest<ShipMethodDto[]>(`/v1/ship-methods${query}`, {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function createShipMethod(body: ShipMethodRequest): Promise<ShipMethodDto> {
  const { data } = await apiRequest<ShipMethodDto>('/v1/ship-methods', {
    method: 'POST',
    body,
    ...COOKIE_INIT,
  });
  return data;
}

export async function updateShipMethod(
  id: string,
  body: ShipMethodRequest,
): Promise<ShipMethodDto> {
  const { data } = await apiRequest<ShipMethodDto>(
    `/v1/ship-methods/${encodeURIComponent(id)}`,
    { method: 'PUT', body, ...COOKIE_INIT },
  );
  return data;
}

export async function deleteShipMethod(id: string): Promise<void> {
  await apiRequest<void>(`/v1/ship-methods/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    ...COOKIE_INIT,
  });
}

/** How a carrier reads in a picker: `UPSG — UPS Ground`. */
export function shipMethodLabel(method: ShipMethodDto): string {
  return `${method.shipMethodCode} — ${method.displayName}`;
}
